import { StateParams, StateService } from "@uirouter/angularjs";
import {
  MultipleMachineChangeChildStateServiceProvider,
  vsMultiProviderName as MultipleMachineChangeChildStateServiceProviderName,
  SingleMachineChangeChildStateServiceProvider,
  vsSingleProviderName as SingleMachineChangeChildStateServiceProviderName,
} from "../../../shared/components/machineChange";
import { vsComponentName as MachineEditFormComponentName } from "../../components/machineEditForm";
import {
  MachineAndConfigurationThreeRivers,
  MachineThreeRivers,
} from "../../models/machine";
import {
  CloneAndConversionServiceThreeRivers,
  vsServiceName as CloneAndConversionServiceName,
} from "../../services/cloneAndConversionService";
import { cloneDeep } from "lodash";
import {
  FormViewController,
  FormViewService,
  vsName as FormViewServiceName,
} from "../../../../records/forms/formView";
import { vsComponentName as machinesMoveViewComponentName } from "../../components/views/main/move";
import { vsComponentName as machinesWarehouseViewComponentName } from "../../components/views/main/warehouse";
import { vsComponentName as machinesDecommissionMoveViewComponentName } from "../../components/views/main/decommission";
import { vsComponentName as singleMachineMoveComponentName } from "../../components/views/detail/move";
import { vsComponentName as singleMachineWarehouseComponentName } from "../../components/views/detail/warehouse";
import { vsComponentName as singleMachineDecommissionComponentName } from "../../components/views/detail/decommission";

export function changesConfig(
  singleChangeProvider: SingleMachineChangeChildStateServiceProvider,
  multiChangeProvider: MultipleMachineChangeChildStateServiceProvider
) {
  multiChangeProvider
    .addElement({
      displayName: "Clone",
      id: "clone",
      cssClass: "fa fa-clone",
      requiredPermission: "machineEdit",
      uiSrefFragment: "add",
    })
    .addElement({
      displayName: "Convert",
      id: "conversion",
      cssClass: "fa fa-cogs",
      requiredPermission: "machineConvert",
      uiSrefFragment: "main.machines.detail.configurations.conversion",
      absoluteSref: true,
    })
    .addElement({
      displayName: "Move",
      id: "move",
      cssClass: "fa fa-arrows",
      requiredPermission: "machineMove",
      uiSrefFragment: "move",
      stateDefinition: {
        component: machinesMoveViewComponentName,
        url: "/move",
        params: {
          machineConfigurations: undefined,
        },
        resolve: {
          machineConfigurations: resolveMachineConfigurations,
        },
      },
    })
    .addElement({
      displayName: "Warehouse",
      id: "warehouse",
      cssClass: "fa fa-archive",
      requiredPermission: "machineWarehouse",
      uiSrefFragment: "warehouse",
      stateDefinition: {
        url: "/warehouse",
        component: machinesWarehouseViewComponentName,
        params: { machineConfigurations: undefined },
        resolve: { machineConfigurations: resolveMachineConfigurations },
      },
    })
    .addElement({
      displayName: "Decommission",
      id: "decommission",
      cssClass: "fa fa-recycle",
      requiredPermission: "machineDecommission",
      uiSrefFragment: "decommission",
      stateDefinition: {
        url: "/decommission",
        component: machinesDecommissionMoveViewComponentName,
        params: { machineConfigurations: undefined },
        resolve: { machineConfigurations: resolveMachineConfigurations },
      },
    });

  singleChangeProvider
    .addElement({
      displayName: "Clone",
      id: "clone",
      cssClass: "fa fa-clone",
      requiredPermission: "machineEdit",
      uiSrefFragment: "main.machines.add",
      absoluteSref: true,
    })
    .addElement({
      displayName: "Convert",
      id: "conversion",
      cssClass: "fa fa-cogs",
      requiredPermission: "machineConvert",
      uiSrefFragment: "conversion",
      stateDefinition: {
        url: "/conversion",
        component: MachineEditFormComponentName,
        params: {
          modelMachineId: undefined,
          modelMachineConfigurationId: undefined,
        },
        resolve: {
          model: [
            "machine",
            "$stateParams",
            CloneAndConversionServiceName,
            (
              machine: MachineThreeRivers,
              $stateParams: StateParams,
              cloneAndConversionService: CloneAndConversionServiceThreeRivers
            ) => {
              let modelMachineId: number | undefined =
                $stateParams.modelMachineId;
              let modelMachineConfigurationId: number | undefined =
                $stateParams.modelMachineConfigurationId;

              const currentConfiguration = machine.Configs!.find(
                (c) => c.CurrentConfiguration
              )!;

              const modelMachine = cloneDeep(machine ?? {});
              delete modelMachine.Configs;

              return cloneAndConversionService
                .getConversionConfiguration(
                  currentConfiguration,
                  modelMachineId,
                  modelMachineConfigurationId
                )
                .then((conversionConfiguration) => {
                  return {
                    Machine: modelMachine,
                    Configuration: conversionConfiguration,
                  };
                });
            },
          ],
          machineFieldsEditable: [() => false],
          onSubmit: [
            FormViewServiceName,
            "$state",
            (formViewService: FormViewService, $state: StateService) =>
              (
                formViewController: FormViewController,
                model: MachineAndConfigurationThreeRivers
              ) => {
                return formViewService
                  .submitForm({
                    formObject: model.Configuration,
                    formViewController,
                    method: "POST",
                    url: `/api/machines/${model.Machine.Id}/conversion`,
                    validationPrefix: "Configuration",
                  })
                  .then((result) =>
                    $state.go("^", undefined, { reload: true })
                  );
              },
          ],
          onCancel: ["$state", ($state: StateService) => () => $state.go("^")],
          formTitle: [() => "Conversion"],
          isConversion: [() => true],
        },
      },
    })
    .addElement({
      displayName: "Move",
      id: "move",
      cssClass: "fa fa-arrows",
      requiredPermission: "machineMove",
      uiSrefFragment: "move",
      stateDefinition: {
        url: "/move",
        component: singleMachineMoveComponentName,
      },
    })
    .addElement({
      displayName: "Warehouse",
      id: "warehouse",
      cssClass: "fa fa-archive",
      requiredPermission: "machineWarehouse",
      uiSrefFragment: "warehouse",
      stateDefinition: {
        url: "/warehouse",
        component: singleMachineWarehouseComponentName,
      },
    })
    .addElement({
      displayName: "Decommission",
      id: "decommission",
      cssClass: "fa fa-recycle",
      requiredPermission: "machineDecommission",
      uiSrefFragment: "decommission",
      stateDefinition: {
        url: "/decommission",
        component: singleMachineDecommissionComponentName,
      },
    });
}
changesConfig.$inject = [
  SingleMachineChangeChildStateServiceProviderName,
  MultipleMachineChangeChildStateServiceProviderName,
];

const resolveMachineConfigurations = [
  "$stateParams",
  ($stateParams: StateParams) => $stateParams.machineConfigurations,
];
